import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Pagination from '../../../components/Pagination/Pagination'
import UserSlug from '../../../components/slugs/UserSlug/UserSlug'
import { userCanCommentOnRoot } from '../../../state/calculations/permissions/userCan'
import useComments from '../../../state/queries/useComments'
import { useSession } from '../../../state/session'
import useRoutes from '../../../state/useRoutes'
import { Comment, CommentRoot, CommentRootUuid } from '../../../models/Comment'
import useDialog from '../../../utilities/useDialog'
import Button from '../../basics/Button/Button'
import CreateThreadForm from '../CreateThreadForm/CreateThreadForm'
import EnterTopic from '@material-symbols/svg-300/rounded/login.svg'

import styles from './ThreadsDisplay.module.scss'

/**********************************************************************
 * CommentDisplay
 **********************************************************************/
const CommentDisplay = React.memo(function CommentDisplay({
  comment,
  root: _root,
}: {
  comment: Comment
  root: CommentRoot
}) {
  const { t } = useTranslation()
  const routes = useRoutes()

  const user = comment.user

  const replyCount = useMemo(() => {
    const rec = (c: Comment): number => 1 + (c.children || []).reduce((acc, c) => acc + rec(c), 0)
    return rec(comment) - 1
  }, [comment])

  const url = comment.learningItemUuid
    ? routes.courseComment.url({ uuid: comment.learningItemUuid, commentUuid: comment.uuid })
    : routes.groupComment.url({ uuid: comment.groupUuid!, commentUuid: comment.uuid })

  return (
    <section className={styles.CommentDisplay}>
      <header>
        <h1>
          <Link to={url}>
            {comment.deletedAt ? (
              <span className={styles.deleted}>{t('comment.deleted comment title')}</span>
            ) : (
              comment.title
            )}
            <EnterTopic className={styles.enterTopicIcon} />
          </Link>
        </h1>
        <div>
          <UserSlug user={user as any} />
          <time dateTime={comment.createdAt.toISOString()}>
            {t('format.DATETIME', { datetime: comment.createdAt })}
          </time>
          <span>{t('comment.{count} replies', { count: replyCount })}</span>
        </div>
      </header>
      <main>{comment.text}</main>
    </section>
  )
})

/**********************************************************************
 * ThreadsDisplay
 **********************************************************************/
type ThreadsDisplayProps = { root: CommentRoot } & CommentRootUuid

export default React.memo(function ThreadsDisplay({ root, ...itemUuid }: ThreadsDisplayProps) {
  const { t } = useTranslation()
  const showDialog = useDialog()
  const { user: currentUser } = useSession()

  const { comments, ...commentsPaging } = useComments(itemUuid)

  const handleCreateThread = useCallback(
    () => showDialog<void>((close) => <CreateThreadForm {...itemUuid} onClose={close} />),
    [itemUuid]
  )

  const canCreateThread = userCanCommentOnRoot(currentUser, root)

  return (
    <section className={styles.ThreadsDisplay}>
      <header>
        <h1>{t('comment.field comments')}</h1>
        {canCreateThread && (
          <Button filled small onClick={handleCreateThread}>
            {t('comment.cta new thread')}
          </Button>
        )}
      </header>

      <main>
        <ul className={styles.comments}>
          {comments.map((comment) => (
            <li key={comment.uuid}>
              <CommentDisplay comment={comment} root={root} />
            </li>
          ))}
          {!comments.length && <li className={styles.empty}>{t('comment.comments empty')}</li>}
        </ul>
        <Pagination {...commentsPaging} onPageChanged={commentsPaging.setPage} />
      </main>
    </section>
  )
})
